import { customerProfileService } from 'app/shared/services/customer-profile/customerProfile-service';
import { ErrorMessage, ErrorType } from 'app/shared/models/errorModal.model';
import { FlowType } from 'app/shared/models/flow-details.model';
import { ProductCategory } from 'app/shared/models/product-category.model';

class ErrorMessageService {
  public getErrorMessageData(errorType: ErrorType, productCategory?: ProductCategory, flowType?: FlowType): ErrorMessage {
    productCategory = productCategory ?? 'nbn';
    const productCategoryLabel = productCategory === 'nbn' ? 'internet' : productCategory;
    // TODO: contactUrl to be updated
    const contactUrl = productCategory === 'nbn' ? '/help-support/contact-us' : '';
    const errorMessageData: ErrorMessage = {
      errorTitle: 'Something went wrong.',
      errorMessage: `Sorry, we’re having trouble showing our ${productCategoryLabel} right now. Please try again soon.`,
      contactType: 'redirect',
      contactUrl,
      showFooter: true,
      icon: 'nbn'
    };

    if (flowType === 'bundle-flow') {
      errorMessageData.buttonLabel = 'Continue with energy';
      errorMessageData.buttonUrl = '/get-connected/electricity-gas-plans/vic/comparison?cidi=hp-ql-cp';
      switch (errorType) {
        case 'serviceNotAvailable':
          errorMessageData.errorMessage =
            'Sorry, we can’t connect an nbn service at your address right now. But we can get your energy connected.';
          break;
        case 'multipleMatching':
          errorMessageData.errorMessage =
            'We’ve found more than one property that matches the address you entered. Please try again or continue with energy only.';
          break;
        case 'systemError':
          errorMessageData.errorMessage = `Sorry, we’re having trouble showing our ${productCategoryLabel} plans right now. But we can get your energy connected.`;
          break;
        default:
          break;
      }
    } else if (flowType === 'guided-flow') {
      const path = window.location.origin + window.location.pathname.replace(/\/+$/, '');
      const buttonUrl = `${path}?cidi=A10846`;
      errorMessageData.buttonLabel = 'Try again';
      errorMessageData.buttonUrl = buttonUrl;
      errorMessageData.contactType = 'redirect';
      errorMessageData.forceRefresh = true;
      switch (errorType) {
        case 'multipleMatching':
          errorMessageData.errorMessage =
            'We’ve found more than one property that matches the address you entered. Please try again or continue with energy only.';
          break;
        case 'systemError':
          errorMessageData.errorMessage =
            'Sorry, we’re having trouble showing our plans right now. Please try again. If you have any questions or need more help, please contact us.';
          break;
        default:
          break;
      }
    } else if (flowType === 'movers-flow') {
      if (errorType === 'systemError') {
        errorMessageData.buttonUrl = '/apps/confirm/?#/movers/address';
      }

      switch (errorType) {
        case 'serviceNotAvailable':
          errorMessageData.errorTitle = 'Unable to connect you';
          errorMessageData.errorMessage = 'Unfortunately we cannot connect your address to the ';
          errorMessageData.errorMessageExtended =
            ' network at this stage. This might be because your address is not serviceable by us or by NBN Co.';
          errorMessageData.secondErrorMessage = 'Check your address with NBN Co to see what options are available.';
          errorMessageData.contactType = null;
          errorMessageData.buttonLabel = 'Check address';
          errorMessageData.buttonUrl = 'https://www.nbnco.com.au/connect-home-or-business/check-your-address?';
          errorMessageData.buttonOpenNewWindow = true;
          break;
        case 'multipleMatching':
          errorMessageData.errorMessage =
            'We’ve found more than one property that matches the address you entered. Please try again or continue with energy only.';
          break;
        case 'systemError':
          errorMessageData.errorTitle = 'Please try again later';
          errorMessageData.errorMessage = `Sorry we’re having trouble showing our internet plans right now. Please try again soon.`;
          errorMessageData.buttonLabel = 'Try again';
          errorMessageData.buttonUrl = '/apps/confirm/?#/movers/address';
          break;
        default:
          break;
      }
    } else if (flowType === 'movers-bundle-flow') {
      const customerProfile = customerProfileService.getCustomerProfile();

      if (errorType === 'serviceNotAvailable' && customerProfile?.isMobileUpdated) {
        errorMessageData.buttonUrl = '/residential/mobile/moving';
      } else {
        errorMessageData.buttonUrl = '/cart?cidi=A11097';
      }
      switch (errorType) {
        case 'serviceNotAvailable':
          errorMessageData.errorTitle = 'Unable to connect internet';
          errorMessageData.errorMessage = 'Unfortunately, we cannot connect your new address to the ';
          errorMessageData.errorMessageExtended =
            ' service at this stage. This might be because your new address is not serviceable by us or by NBN Co.';
          errorMessageData.contactType = 'moversContactUs';
          errorMessageData.buttonLabel = 'Continue without internet';
          break;
        case 'multipleMatching':
          errorMessageData.errorMessage =
            'We’ve found more than one property that matches the address you entered. Please try again or continue with energy only.';
          break;
        case 'systemError':
          errorMessageData.errorTitle = 'Please try again later';
          errorMessageData.errorMessage = `Sorry we’re having trouble showing our internet plans right now. Please try again soon.`;
          errorMessageData.buttonLabel = 'Try again';
          errorMessageData.buttonUrl = '/apps/confirm/?#/movers/address';
          break;
        default:
          break;
      }
    } else {
      errorMessageData.forceRefresh = true;
      const path = window.location.origin + window.location.pathname.replace(/\/+$/, '');
      const buttonUrl = `${path}?cidi=A10846`;
      switch (errorType) {
        case 'hfcServiceNotAvailable':
          errorMessageData.errorMessage = `Unfortunately, because your address has an nbn HFC connection type, we’re not currently able to connect you. This is due to a global shortage of available chipsets for HFC NTD boxes experienced by nbn co. More information can be found in the section below called `;
          errorMessageData.contactType = 'getInTouch';
          errorMessageData.buttonLabel = 'Contact us';
          errorMessageData.buttonUrl = contactUrl;
          errorMessageData.buttonOpenNewWindow = true;
          errorMessageData.messageUrl = `${path}#hfc`;
          errorMessageData.messageLinkText = 'Non-serviceable HFC addresses';
          errorMessageData.forceRefresh = false;
          break;
        case 'serviceNotAvailable':
          errorMessageData.errorTitle = 'Unable to connect you';
          errorMessageData.errorMessage = 'Unfortunately we cannot connect your address to the ';
          errorMessageData.errorMessageExtended =
            ' network at this stage. This might be because your address is not serviceable by us or by NBN Co.';
          errorMessageData.secondErrorMessage = 'Check your address with NBN Co to see what options are available.';
          errorMessageData.contactType = null;
          errorMessageData.buttonLabel = 'Check address';
          errorMessageData.buttonUrl = 'https://www.nbnco.com.au/connect-home-or-business/check-your-address?';
          errorMessageData.buttonOpenNewWindow = true;
          break;
        case 'multipleMatching':
          errorMessageData.errorMessage = 'We’ve found more than one property that matches the address you entered.';
          errorMessageData.contactType = 'tryAgainOrRedirect';
          errorMessageData.buttonLabel = 'Try again';
          errorMessageData.buttonUrl = buttonUrl;
          break;
        case 'systemError':
          errorMessageData.errorMessage = `Sorry, we’re having trouble showing our ${productCategoryLabel} plans right now. Please try again soon.`;
          errorMessageData.buttonLabel = 'Try again';
          errorMessageData.buttonUrl = buttonUrl;
          break;
        default:
          break;
      }
    }
    return errorMessageData;
  }
}

export const errorMessageService = new ErrorMessageService();
